import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { DisbursementReport } from "@/services/financeService";
import { formatMoney } from "./format";

const fg = (n: number) => formatMoney(n);

export interface DisbursementPdfOptions {
  schoolName: string;
  schoolYear: string;
  scopeLabel: string;
}

/**
 * Génère et télécharge le « Tableau prévisionnel des décaissements » (prévu vs
 * réalisé) en PDF. Les couleurs sont volontairement fixes (RGB) car un PDF ne
 * peut pas lire les variables de thème CSS.
 */
export function exportDisbursementPdf(report: DisbursementReport, options: DisbursementPdfOptions): void {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(29, 95, 208);
  doc.rect(40, 34, pageWidth - 80, 34, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(
    `TABLEAU PRÉVISIONNEL DES DÉCAISSEMENTS DU ${options.schoolName.toUpperCase()}`,
    pageWidth / 2,
    56,
    { align: "center" }
  );

  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Session : ${options.schoolYear}`, 40, 84);
  if (options.scopeLabel) {
    doc.text(options.scopeLabel, pageWidth - 40, 84, { align: "right" });
  }

  const body: string[][] = [];
  const kinds: string[] = [];

  report.groups.forEach((group) => {
    body.push([group.id, group.title, "", "", "", ""]);
    kinds.push("group");
    group.lines.forEach((line, i) => {
      body.push([String(i + 1), line.label, fg(line.monthly), fg(line.annual), fg(line.actual), fg(line.gap)]);
      kinds.push("item");
    });
    body.push([
      "",
      `Total ${group.title}`,
      fg(group.totalMonthly),
      fg(group.totalAnnual),
      fg(group.totalActual),
      fg(group.totalGap),
    ]);
    kinds.push("subtotal");
  });
  body.push([
    "",
    "Total des charges à décaisser",
    fg(report.grandMonthly),
    fg(report.grandAnnual),
    fg(report.grandActual),
    fg(report.grandGap),
  ]);
  kinds.push("grand");

  autoTable(doc, {
    startY: 98,
    head: [["N°", "CHARGES PRÉVISIONNELLES", "Prévu mensuel", "Prévu annuel", "Réalisé", "Écart"]],
    body,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      cellPadding: 5,
      textColor: [55, 55, 52],
      lineColor: [206, 206, 200],
      lineWidth: 0.5,
    },
    headStyles: {
      fillColor: [234, 238, 245],
      textColor: [55, 60, 55],
      fontStyle: "bold",
      fontSize: 9,
    },
    columnStyles: {
      0: { cellWidth: 30, halign: "center" },
      1: { cellWidth: "auto" },
      2: { cellWidth: 92, halign: "right" },
      3: { cellWidth: 92, halign: "right" },
      4: { cellWidth: 92, halign: "right" },
      5: { cellWidth: 92, halign: "right" },
    },
    didParseCell: (data: any) => {
      if (data.section !== "body") return;
      const kind = kinds[data.row.index];
      if (kind === "group") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [243, 245, 240];
      } else if (kind === "subtotal") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [240, 242, 238];
      } else if (kind === "grand") {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [226, 236, 252];
        data.cell.styles.textColor = [14, 42, 92];
      }
    },
  });

  const stamp = new Date().toISOString().slice(0, 10);
  doc.save(`tableau-decaissements-${options.schoolYear}-${stamp}.pdf`);
}